import 'simplebar';
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import FloatingVue from 'floating-vue';
import Toast, { POSITION } from 'vue-toastification';
import { addMethod, string } from 'yup';
import { toUnicode } from 'punycode';

import { Chart } from 'chart.js';
import VueGtag from 'vue-gtag';
import App from '@/App.vue';
import router from '@/router';
import { i18n, t } from '@/lib/i18n';
import '@/lib/icons';
import '@/lib/analytics';

import 'floating-vue/dist/style.css';
import '@/assets/styles/index.css';
import '@vueform/multiselect/themes/default.css';
import 'instantsearch.css/themes/reset-min.css';
import { is44zero, platformCode } from '@/lib/platform';
import { head } from '@/lib/head';

if (!is44zero) {
  import(`@/assets/styles/white-label/${platformCode}.css`);
}

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.75 : 1.0,
    tracePropagationTargets: ['localhost', /^\//],
    logErrors: import.meta.env.DEV,
    enabled: import.meta.env.PROD,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
  });
}

// see https://vitejs.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

app.use(router);
app.use(i18n);
app.use(FloatingVue, {
  themes: {
    popover: {
      $extend: 'dropdown',
      $resetCss: true,
      distance: -4,
    },
    'secondary-tooltip': {
      $extend: 'tooltip',
    },
    tooltip: {
      autoHide: true,
    },
  },
  disposeTimeout: 0,
});
app.use(Toast, {
  position: POSITION.TOP_RIGHT,
});

app.use(head);

if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
  app.use(VueGtag, {
    enabled: false,
    bootstrap: false,
    pageTrackerUseFullPath: true,
    config: {
      id: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
    },
  }, router);
}

// eslint-disable-next-line vue/component-definition-name-casing
app.component('font-awesome-icon', FontAwesomeIcon);
// eslint-disable-next-line vue/component-definition-name-casing
app.component('font-awesome-layers', FontAwesomeLayers);

document.documentElement.classList.add(`platform--${platformCode}`);

Chart.defaults.font.family = "'Sora', sans-serif";

function ensureUnicodeEmail(input: string | null | undefined) {
  if (!input || input === '') {
    return null;
  }

  return toUnicode(input);
}

// eslint-disable-next-line func-names
addMethod(string, 'email', function () {
  // email regex from yup: https://github.com/jquense/yup/blob/v1.6.1/src/string.ts#L20
  // eslint-disable-next-line max-len, no-useless-escape, vue/max-len
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  return this.test({
    name: 'email',
    message: t('generic.validations.email'),
    skipAbsent: true,
    test: (value) => {
      // ensure unicode value as e.g. Chrome converts it to punycode, so e.g. example@täst.de would become example@xn--tst-qla.de
      const valueForCheck = ensureUnicodeEmail(value);

      if (!valueForCheck) {
        return true;
      }

      return valueForCheck.search(emailRegex) !== -1;
    },
  });
});

app.mount('#app');
